// KPI Builder — turns the admin's answers into the stored query and additionalConfig.
import { ALL_GROUPS, CHARTS, RECIPES } from 'constants/kpiBuilderCatalogue';
import { groupById, rateCandidates } from './learnerGroups';
import {
  ANCHORED, chosenKpiFilters, effectiveCumulative, groupOf, listColumns, measureOf, recipeSources, selectedGroups, source, sourceB, sourcesReady,
} from './lookups';
import { canPickAnyChart, isOtherChart } from './chartChoices';
import { isFunnelComplete } from './funnel';
import { compileSpecial } from './specialCompile';
import { snapshotOf, toQueryString } from './serialize';
import { isAdvancedReady } from './advanced';
import { isFilterReady } from './custom';
import { isTemplateComplete } from './templateKpi';
import { applyExtras } from './extras';
import { PROGRESS_KINDS, progressTitle } from './progressRecipes';
import { READY_KINDS, readyTitle, isReadyComplete } from './readyRecipes';
import { isRangeReady } from './rangeRecipe';
import { buildGapDetail } from './gapDetail';
import { RecipeContext } from './queries';
import { QueryRecipeId, RECIPE_BUILDERS } from './recipes';
import { bucketsOf, seriesNames } from './seriesRecipes';
import {
  BuilderAnswers, BuilderConfig, EMPTY_ANSWERS, EsNode, MetricAgg, BuilderOutput, ChartOption, DataSource, GroupBy, LearnerGroup, Measure, RecipeId, SourceId,
} from './types';

export * from './lookups';
// ---------- completeness ----------
const customDataReady = (a: BuilderAnswers): boolean => a.src !== 'custom' || (a.custom.source.index.trim() !== '' && a.custom.source.date.trim() !== '');

const SELF_CONTAINED: RecipeId[] = ['funnel', 'template', 'advanced'];

export function isSourceComplete(a: BuilderAnswers): boolean {
  if (!a.recipe) return false;
  if (SELF_CONTAINED.includes(a.recipe)) return true;
  if (ANCHORED.includes(a.recipe)) return sourcesReady(a);
  return !!a.src && recipeSources(a).includes(a.src) && customDataReady(a);
}

/** A trait is ready when it is a saved learner group that fits, or a typed condition that is finished. */
const traitReady = (a: BuilderAnswers): boolean => (a.rateSeg === 'custom' ? isFilterReady(a.custom.rateWhen) : rateCandidates(a).some((g) => g.id === a.rateSeg));

const COMPLETE: Record<RecipeId, (a: BuilderAnswers) => boolean> = {
  count: (a) => !!measureOf(a),
  perGroup: (a) => !!measureOf(a) && !!groupOf(a),
  trend: (a) => !!measureOf(a),
  rate: (a) => !!groupOf(a) && traitReady(a),
  gap: () => true,
  list: (a) => listColumns(a).length > 0,
  compare: (a) => !!measureOf(a) && !!groupOf(a) && traitReady(a),
  share: (a) => !!groupOf(a) && bucketsOf(a, source(a)?.buckets ?? []).length >= 2,
  funnel: (a) => isFunnelComplete(a.funnel),
  questions: () => true,
  template: (a) => isTemplateComplete(a.template),
  ranges: (a) => isRangeReady(a.custom.rangeField, a.custom.ranges),
  progress: () => true,
  ready: (a) => isReadyComplete(a),
  advanced: (a) => isAdvancedReady(a.advanced),
};

export function isDataComplete(a: BuilderAnswers): boolean {
  return isSourceComplete(a) && COMPLETE[a.recipe as RecipeId](a);
}

// ---------- charts ----------
/** Every chart, plus the one a hand-written KPI already uses when it is not in the list. */
const withChosen = (all: ChartOption[], chosen: string | null): ChartOption[] =>
  (chosen && !all.some((c) => c.id === chosen) ? [...all, { id: chosen, label: chosen, desc: 'The chart type this KPI already uses.' }] : all);

const CHART_OPTIONS: Record<RecipeId, (a: BuilderAnswers) => ChartOption[]> = {
  count: () => [CHARTS.metric],
  perGroup: (a) => [CHARTS.horizontalbar, CHARTS.bar, CHARTS.donut, CHARTS.pie].concat(a.group === 'district' ? [CHARTS.indiadistrictmap] : []),
  trend: () => [CHARTS.line, CHARTS.bar],
  rate: () => [CHARTS.horizontalbar],
  gap: () => [CHARTS.notenrolledanalysis],
  list: () => [CHARTS.table],
  compare: () => [CHARTS.multibar],
  share: () => [CHARTS.stackedbar],
  funnel: (a) => [a.funnel.kind === 'stage' ? CHARTS.kpistagefunnel : CHARTS.conversionfunnel],
  questions: (a) => [a.questionKind === 'cohort' ? CHARTS.heatmap : CHARTS.horizontalbar],
  ranges: () => [CHARTS.histogram],
  progress: (a) => [a.progressKind === 'course' ? CHARTS.stackedbar : CHARTS.multibar],
  ready: (a) => [CHARTS[READY_KINDS[a.readyKind].chart]],
  advanced: (a) => withChosen(Object.values(CHARTS), a.chart),
  template: (a) => (a.template.chartType ? [{ id: a.template.chartType, label: a.template.chartType, desc: 'Same chart as the KPI you copied.' }] : []),
};

export const chartOptions = (a: BuilderAnswers): ChartOption[] => (a.recipe ? CHART_OPTIONS[a.recipe](a) : []);

/** The chosen chart if still valid (any plain chart for most questions), otherwise the recommended (first) one. */
export function resolvedChart(a: BuilderAnswers): string | null {
  const ids = chartOptions(a).map((c) => c.id);
  const chosenIsValid = !!a.chart && (ids.includes(a.chart) || (canPickAnyChart(a) && isOtherChart(a.chart)));
  return chosenIsValid ? a.chart : ids[0] ?? null;
}

// ---------- titles, codes and descriptions ----------
interface Facts { a: BuilderAnswers; s: DataSource; m: Measure; g: GroupBy; sel: LearnerGroup[]; }

const cap = (w: string): string => w.charAt(0).toUpperCase() + w.slice(1);
const rateLabel = (a: BuilderAnswers): string => (a.rateSeg === 'custom' ? a.custom.rateLabel || 'matching' : groupById(a.rateSeg as string)?.label ?? '').toLowerCase();
const topWord = (n: number): string => (n >= ALL_GROUPS ? 'all' : `the top ${n}`);
const INTERVAL_WORD = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

const TITLES: Record<RecipeId, (f: Facts, seg: string) => string> = {
  count: (f, seg) => `${f.m.label} - ${f.s.label}${seg}`,
  perGroup: (f, seg) => `${f.a.topN >= ALL_GROUPS ? 'All' : `Top ${f.a.topN}`} ${f.g.label.toLowerCase()}s by ${f.m.label.toLowerCase()}${seg}`,
  trend: (f) => `${INTERVAL_WORD[f.a.interval]} ${f.m.label.toLowerCase()} - ${f.s.label.toLowerCase()}${effectiveCumulative(f.a) ? ' (running total)' : ''}`,
  rate: (f, seg) => `% ${rateLabel(f.a)} by ${f.g.label.toLowerCase()}${seg}`,
  gap: (f, seg) => `${cap(f.s.word)} but not yet ${sourceB(f.a)?.word}${seg}`,
  list: (f, seg) => `${cap(f.s.word)} learners - list${seg}`,
  compare: (f, seg) => `${seriesNames(f.a).join(' vs ')} by ${f.g.label.toLowerCase()}${seg}`,
  share: (f, seg) => `${f.s.label} split by ${f.g.label.toLowerCase()}${seg}`,
  funnel: (f) => `${f.a.funnel.stages.map((s) => s.label).join(' to ')} funnel`,
  template: (f) => `${f.a.template.title} (copy)`,
  progress: (f, seg) => `${progressTitle(f.a)}${seg}`,
  ready: (f, seg) => `${readyTitle(f.a)}${seg}`,
  advanced: () => 'Custom KPI',
  ranges: (f, seg) => `${f.s.label} spread by ${f.a.custom.rangeField}${seg}`,
  questions: (f, seg) => `${f.a.questionKind === 'cohort' ? 'Question quality by cohort' : 'Question difficulty'}${seg}`,
};

const DESCRIPTIONS: Record<RecipeId, (f: Facts, w: string) => string> = {
  count: (f, w) => `Counts ${f.m.label.toLowerCase()} in ${f.s.label.toLowerCase()}${w}.`,
  perGroup: (f, w) => `Ranks ${topWord(f.a.topN)} ${f.g.label.toLowerCase()}s by ${f.m.label.toLowerCase()} in ${f.s.label.toLowerCase()}${w}.`,
  trend: (f, w) => `Shows ${f.m.label.toLowerCase()} in ${f.s.label.toLowerCase()} per ${f.a.interval}${effectiveCumulative(f.a) ? ' as a running total' : ''}${w}.`,
  rate: (f, w) => `For each ${f.g.label.toLowerCase()}, the % of learners who are ${rateLabel(f.a)}${w}, highest first.`,
  gap: (f, w) => `Counts learners who are ${f.s.word} but not ${sourceB(f.a)?.word}${w}, with a clickable list.`,
  list: (f, w) => `Lists ${f.s.word} learners${w} with ${listColumns(f.a).map((c) => c.label.toLowerCase()).join(', ')}.`,
  compare: (f, w) => `For each ${f.g.label.toLowerCase()}, shows ${seriesNames(f.a).join(' next to ')}${w}, side by side.`,
  questions: (f, w) => (f.a.questionKind === 'cohort'
    ? `For each question, the share answered correctly on the first attempt, split by learners who went on to pass or fail${w}.`
    : `For each question, the share of answers that were correct${w}, so the hardest questions stand out.`),
  progress: (f, w) => `${PROGRESS_KINDS[f.a.progressKind].summary}${w}.`,
  ready: (f, w) => `${READY_KINDS[f.a.readyKind].summary}${w}.`,
  advanced: () => 'A KPI built part by part: its queries, aggregations and settings were written by hand.',
  ranges: (f, w) => `Counts ${f.s.label.toLowerCase()}${w} in each range of ${f.a.custom.rangeField}: ${f.a.custom.ranges.map((r) => r.label).join(', ')}.`,
  template: (f) => `A copy of “${f.a.template.title}”, with its query and settings kept as they are${f.a.template.cadre === 'keep' ? '' : ' and the cadre switched'}.`,
  funnel: (f) => `Connects ${f.a.funnel.stages.map((s) => s.label).join(', then ')} as a ${f.a.funnel.kind === 'stage' ? 'step-by-step' : 'conversion'} funnel, showing the share that moves on at each step.`,
  share: (f, w) => `For each ${f.g.label.toLowerCase()}, shows how ${f.s.label.toLowerCase()}${w} split across ${bucketsOf(f.a, f.s.buckets ?? []).map((b) => b.label).join(', ')}, as a share of 100%.`,
};

const factsOf = (a: BuilderAnswers): Facts => ({
  a, s: source(a) as DataSource, m: measureOf(a) as Measure, g: groupOf(a) as GroupBy, sel: selectedGroups(a),
});

export function autoTitle(a: BuilderAnswers): string {
  if (!isDataComplete(a)) return '';
  const f = factsOf(a);
  return TITLES[a.recipe as RecipeId](f, f.sel.length ? ` (${f.sel[0].label})` : '');
}

/** camelCase code from the title; always starts with a letter. */
export function codeFromTitle(title: string): string {
  if (!title || !title.replace(/[^A-Za-z0-9]/g, '')) return 'newVisualization';
  const raw = title.replace(/[^A-Za-z0-9 ]/g, ' ').split(' ').filter(Boolean)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())).join('');
  return /^[a-z]/.test(raw) ? raw : 'kpi' + raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Plain-language description shown in the summary panel. */
export function describe(a: BuilderAnswers): string {
  if (!isDataComplete(a)) {
    const recipe = RECIPES.find((r) => r.id === a.recipe);
    return recipe ? `${recipe.label} Keep going in the Data tab.` : 'Start in the Data tab by choosing what you want to know.';
  }
  const f = factsOf(a);
  const w = f.sel.length ? ' for ' + f.sel.map((x) => x.label).join(' + ').toLowerCase() : '';
  return DESCRIPTIONS[a.recipe as RecipeId](f, w) + ' Dashboard date and location filters still apply.';
}

// ---------- compile ----------
const METRIC_NAMES: Record<MetricAgg, string> = { cardinality: 'unique_users', value_count: 'total_count', avg: 'avg_value', sum: 'sum_value', min: 'min_value', max: 'max_value' };

/** The aggregation for a measure. A divisor turns, say, milliseconds into minutes. */
const metricBody = (m: Measure): EsNode => {
  const scaled = !!m.divisor && m.divisor !== 1 && ['avg', 'sum', 'min', 'max'].includes(m.agg);
  return { [m.agg]: { field: m.field, ...(scaled ? { script: { source: `_value/${m.divisor}.0` } } : {}) } };
};

function contextFor(a: BuilderAnswers, code: string): RecipeContext {
  const m = measureOf(a);
  const metricName = m ? METRIC_NAMES[m.agg] : 'total_count';
  return {
    a, code, chartType: resolvedChart(a) as string, cumulative: effectiveCumulative(a),
    src: source(a) as DataSource, srcId: a.src as SourceId, srcBId: a.srcB as SourceId,
    measure: m, group: groupOf(a) as GroupBy, metric: m ? metricBody(m) : {}, metricName,
  };
}

const LONG_LIST_CHARTS = ['horizontalbar', 'bar', 'stackedbar', 'multibar'];

/** Settings that only some KPIs have: a search box for long lists, and the filters dashboard users can pick. */
function addOptionalSettings(config: BuilderConfig, a: BuilderAnswers, chartType: string): void {
  // Long lists of bars get a search box, as the existing 500-course rating KPIs do.
  if (a.topN > 100 && LONG_LIST_CHARTS.includes(chartType)) config.enableChartSearch = true;
  const kf = chosenKpiFilters(a);
  if (kf.length) config.kpiFilters = kf.map((f) => f.def);
}

/**
 * Build the visualization. Throws if the answers are incomplete — call isDataComplete() first.
 * @param title Title from the Info tab (used for the visualization code unless `code` is given).
 * @param code Existing visualization code, kept unchanged when editing.
 */
export function compile(a: BuilderAnswers, title: string, code?: string): BuilderOutput {
  if (!isDataComplete(a)) throw new Error('KPI Builder: answers are incomplete');
  const special = compileSpecial(a, title, code || codeFromTitle(title), resolvedChart(a) as string);
  if (special) return special;
  const ctx = contextFor(a, code || codeFromTitle(title));
  const { queries, cfg, handlerKey } = RECIPE_BUILDERS[a.recipe as QueryRecipeId](ctx);
  const config: BuilderConfig = {
    action: '', filter: '', insight: {}, documentType: '_doc', drillChart: a.drill || 'none', resultType: 'value', valueType: 'number',
    isDecimal: !!ctx.measure?.decimal, chartType: ctx.chartType, aggregationPaths: [], ...cfg, builder: { ...snapshotOf(a, ctx.cumulative), chart: ctx.chartType },
  };
  addOptionalSettings(config, a, ctx.chartType);
  const detail = a.recipe === 'gap' ? buildGapDetail(ctx, queries, title || ctx.code, toQueryString) : undefined;
  const done = applyExtras(queries, config, handlerKey, a.extras);
  return { visualizationCode: ctx.code, chartType: ctx.chartType, handlerKey: done.handlerKey, queryString: toQueryString(done.queries), config: done.config, queries: done.queries, detail };
}
const pick = <T,>(value: T | null | undefined, fallback: T): T => value ?? fallback;

/** Rebuild answers from a saved config (for editing). Returns null for visualizations not made in the builder. */
export function answersFromConfig(config: Record<string, unknown> | null | undefined): Partial<BuilderAnswers> | null {
  const b = config?.builder as Partial<BuilderConfig['builder']> | undefined;
  if (!b || !b.recipe) return null;
  return {
    recipe: b.recipe, src: pick(b.source, null), srcB: pick(b.sourceB, null), segs: Array.from(new Set(pick(b.learnerGroups, []).map((id) => (id === 'inServiceAnm' ? 'inService' : id)))), measure: pick(b.measure, null),
    group: pick(b.groupBy, null), topN: pick(b.topN, 10), minCount: pick(b.minCount, 1), interval: pick(b.interval, 'day'), cumulative: !!b.cumulative, rateSeg: pick(b.rateGroup, null),
    cols: pick(b.columns, ['name', 'district']), rows: pick(b.rows, 1000), kpiFilters: pick(b.kpiFilters, []), funnel: pick(b.funnel, EMPTY_ANSWERS.funnel), questionKind: pick(b.questionKind, EMPTY_ANSWERS.questionKind), progressKind: pick(b.progressKind, EMPTY_ANSWERS.progressKind), readyKind: pick(b.readyKind, EMPTY_ANSWERS.readyKind), threshold: pick(b.threshold, EMPTY_ANSWERS.threshold), extras: { ...EMPTY_ANSWERS.extras, ...b.extras }, advanced: { ...EMPTY_ANSWERS.advanced, ...b.advanced }, custom: { ...EMPTY_ANSWERS.custom, ...b.custom },
    template: { ...EMPTY_ANSWERS.template, ...b.template },
    chart: pick(b.chart ?? (config?.chartType as string | undefined), null), drill: pick(config?.drillChart as string | undefined, 'none'),
  };
}
